import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useFamilyContext } from './FamilyContext';
import { supabase } from '../lib/supabase';

const CollegePrepContext = createContext();

export const useCollegePrepContext = () => {
  const context = useContext(CollegePrepContext);
  if (!context) {
    throw new Error('useCollegePrepContext must be used within CollegePrepProvider');
  }
  return context;
};

const ACADEMIC_INTERESTS = {
  medicine: {
    label: "Medicine & Healthcare",
    options: ["General Medicine", "Pediatrics", "Public Health", "Medical Research", "Biomedical Engineering", "Global Health"]
  },
  veterinary: {
    label: "Veterinary & Animal Sciences", 
    options: ["Veterinary Medicine", "Animal Behavior", "Wildlife Conservation", "Marine Biology", "Animal Research", "Zoo Sciences"]
  },
  technology: {
    label: "AI & Technology",
    options: ["Artificial Intelligence", "Machine Learning", "Healthcare AI", "Robotics", "Computer Science", "Data Science"]
  },
  socialJustice: {
    label: "Social Justice & Impact",
    options: ["Healthcare Equity", "Environmental Justice", "Community Health", "Policy & Advocacy", "Human Rights", "Ethics in AI"]
  }
};

const EXPERIENCE_GOALS = [
  "Medical Research & Labs",
  "Animal Care & Research", 
  "AI/Tech Projects with Social Impact",
  "Community Health Programs",
  "Leadership in Healthcare",
  "Policy & Advocacy Work",
  "Clinical Shadowing",
  "Veterinary Experience",
  "Public Health Initiatives",
  "Ethics & Philosophy"
];

const PROGRAM_CATEGORIES = {
  medical: {
    label: "Medical & Healthcare",
    programs: ["Hospital research internships", "Public health initiatives", "Global health programs", "Medical ethics workshops", "Community health projects", "Healthcare innovation challenges"]
  },
  veterinary: {
    label: "Veterinary & Animal Science",
    programs: ["Zoo/aquarium internships", "Wildlife conservation programs", "Veterinary shadowing", "Animal research projects", "Marine biology field work", "Animal rescue/rehabilitation"]
  },
  technology: {
    label: "AI & Technology for Good",
    programs: ["AI for healthcare projects", "Tech ethics programs", "Medical AI research", "Social impact tech challenges", "Data science for social good", "Robotics in healthcare"]
  },
  socialJustice: {
    label: "Social Justice & Policy",
    programs: ["Healthcare policy internships", "Community organizing programs", "Environmental justice initiatives", "Human rights advocacy", "Public health equity projects", "Ethics and philosophy programs"]
  }
};

export const CollegePrepProvider = ({ children }) => {
  const { family, currentUser } = useFamilyContext();
  const [interests, setInterests] = useState({});
  const [programs, setPrograms] = useState([]);
  const [researchTasks, setResearchTasks] = useState([]);
  const [discoveryProgress, setDiscoveryProgress] = useState({});
  const [timelineActions, setTimelineActions] = useState([]);
  const [essayDrafts, setEssayDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Convert a JS essay draft to Supabase row format
  const toRow = useCallback((draft, userId) => ({
    id: draft.id,
    user_id: userId,
    prompt_text: draft.promptText || '',
    program_id: draft.programId || null,
    word_limit: draft.wordLimit || null,
    template_id: draft.templateId || null,
    mode: draft.mode || 'guided-response',
    application_group_id: draft.applicationGroupId || null,
    context_notes: draft.contextNotes || '',
    program_url: draft.programUrl || '',
    status: draft.status || 'not_started',
    builder_data: draft.builderData || null,
    content: draft.content || '',
    refinement_analysis: draft.refinementAnalysis || null,
    created_at: draft.createdAt,
    updated_at: draft.updatedAt
  }), []);

  // Convert a Supabase row to JS essay draft format
  const fromRow = useCallback((row) => ({
    id: row.id,
    promptText: row.prompt_text || '',
    programId: row.program_id || null,
    wordLimit: row.word_limit || null,
    templateId: row.template_id || null,
    mode: row.mode || 'guided-response',
    applicationGroupId: row.application_group_id || null,
    contextNotes: row.context_notes || '',
    programUrl: row.program_url || '',
    status: row.status || 'not_started',
    builderData: row.builder_data || null,
    content: row.content || '',
    refinementAnalysis: row.refinement_analysis || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  }), []);

  // Load data: essays from Supabase, rest from localStorage
  useEffect(() => {
    if (!currentUser?.id) return;

    const loadData = async () => {
      setLoading(true);

      // Load non-essay data from localStorage
      const savedData = localStorage.getItem(`beckett-data-${currentUser.id}`);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setInterests(parsed.interests || {});
          setPrograms(parsed.programs || []);
          setResearchTasks(parsed.researchTasks || []);
          setDiscoveryProgress(parsed.discoveryProgress || {});
          setTimelineActions(parsed.timelineActions || []);
        } catch (error) {
          console.error('Error loading localStorage data:', error);
        }
      }

      // Load essays from Supabase, then localStorage fallback
      let essaysLoaded = false;
      try {
        const { data: rows, error } = await supabase
          .from('college_prep_essays')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false });

        if (!error && rows && rows.length > 0) {
          setEssayDrafts(rows.map(fromRow));
          essaysLoaded = true;
        }
      } catch (err) {
        console.error('Error loading essays from Supabase:', err);
      }

      // Fall back to localStorage essays (including backfill to Supabase)
      if (!essaysLoaded && savedData) {
        try {
          const parsed = JSON.parse(savedData);
          const localEssays = parsed.essayDrafts || [];
          if (localEssays.length > 0) {
            setEssayDrafts(localEssays);
            // Try to backfill — will silently fail if table doesn't exist yet
            const rowsToInsert = localEssays.map(d => toRow(d, currentUser.id));
            supabase.from('college_prep_essays').upsert(rowsToInsert).catch(() => {});
            essaysLoaded = true;
          }
        } catch (_) { /* ignore */ }
      }

      // New users simply start with empty state — no sample data

      setInitialLoadDone(true);
      setLoading(false);
    };

    loadData();
  }, [currentUser?.id, fromRow, toRow]);

  // Save non-essay data to localStorage whenever it changes
  useEffect(() => {
    if (!currentUser?.id || !initialLoadDone) return;

    const dataToSave = {
      interests,
      programs,
      researchTasks,
      discoveryProgress,
      timelineActions,
      essayDrafts,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(`beckett-data-${currentUser.id}`, JSON.stringify(dataToSave));
  }, [interests, programs, researchTasks, discoveryProgress, timelineActions, essayDrafts, currentUser?.id, initialLoadDone]);

  const updateInterests = (newInterests) => {
    setInterests(prev => ({ ...prev, ...newInterests }));
  };

  const addProgram = (programData) => {
    const newProgram = {
      id: Date.now().toString(),
      ...programData,
      dateAdded: new Date().toISOString(),
      status: programData.status || 'discovered',
      researchStatus: programData.researchStatus || 'discovered',
      interestMatch: programData.interestMatch || 0,
      personalRatings: {
        competitiveness: 0,
        collegeValue: 0,
        personalFit: 0,
        interestMatch: []
      },
      familyContext: {
        discussedWithParents: false,
        budgetApproved: false,
        needsParentHelp: [],
        calendarIntegrated: false
      },
      collegeRelevance: {
        helpsWithApplications: false,
        demonstratesInterest: "",
        buildsskills: [],
        networkingOpportunities: false
      },
      researchData: {
        applicationRequirements: {},
        programDetails: {},
        competitivenessAssessment: {},
        financialInfo: {},
        contacts: []
      }
    };
    setPrograms(prev => [newProgram, ...prev]);
    return newProgram;
  };

  const updateProgram = (programId, updates) => {
    setPrograms(prev => 
      prev.map(program => 
        program.id === programId ? { ...program, ...updates } : program
      )
    );
  };

  const deleteProgram = (programId) => {
    setPrograms(prev => prev.filter(program => program.id !== programId));
  };

  const addResearchTask = (task) => {
    const newTask = {
      id: Date.now().toString(),
      ...task,
      dateAdded: new Date().toISOString(),
      completed: false
    };
    setResearchTasks(prev => [newTask, ...prev]);
    return newTask;
  };

  const updateResearchTask = (taskId, updates) => {
    setResearchTasks(prev =>
      prev.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      )
    );
  };

  const deleteResearchTask = (taskId) => {
    setResearchTasks(prev => prev.filter(task => task.id !== taskId));
  };

  // Timeline Management
  const addTimelineAction = (action) => {
    const newAction = {
      id: Date.now().toString(),
      ...action,
      dateAdded: new Date().toISOString(),
      completed: false
    };
    setTimelineActions(prev => [newAction, ...prev]);
    return newAction;
  };

  const updateTimelineAction = (actionId, updates) => {
    setTimelineActions(prev =>
      prev.map(action =>
        action.id === actionId ? { ...action, ...updates } : action
      )
    );
  };

  const deleteTimelineAction = (actionId) => {
    setTimelineActions(prev => prev.filter(action => action.id !== actionId));
  };

  // Essay Draft Management
  const addEssayDraft = (draftData) => {
    const newDraft = {
      id: Date.now().toString(),
      promptText: draftData.promptText || '',
      programId: draftData.programId || null,
      wordLimit: draftData.wordLimit || null,
      templateId: draftData.templateId || null,
      mode: draftData.mode || 'guided-response',
      applicationGroupId: draftData.applicationGroupId || null,
      contextNotes: draftData.contextNotes || '',
      programUrl: draftData.programUrl || '',
      status: 'not_started',
      builderData: null,
      content: '',
      refinementAnalysis: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setEssayDrafts(prev => [newDraft, ...prev]);
    if (currentUser?.id) {
      supabase.from('college_prep_essays').insert(toRow(newDraft, currentUser.id)).then(({ error }) => {
        if (error) console.error('Error inserting essay to Supabase:', error);
      });
    }
    return newDraft;
  };

  // Add multiple drafts at once (for multi-question applications)
  const addEssayDraftBatch = (draftsData) => {
    const groupId = Date.now().toString();
    const now = new Date().toISOString();
    const newDrafts = draftsData.map((d, i) => ({
      id: `${groupId}-${i}`,
      promptText: d.promptText || '',
      programId: d.programId || null,
      wordLimit: d.wordLimit || null,
      templateId: d.templateId || null,
      mode: d.mode || 'guided-response',
      applicationGroupId: groupId,
      contextNotes: d.contextNotes || '',
      programUrl: d.programUrl || '',
      status: 'not_started',
      builderData: null,
      content: '',
      refinementAnalysis: null,
      createdAt: now,
      updatedAt: now
    }));
    setEssayDrafts(prev => [...newDrafts, ...prev]);
    if (currentUser?.id) {
      const rows = newDrafts.map(d => toRow(d, currentUser.id));
      supabase.from('college_prep_essays').insert(rows).then(({ error }) => {
        if (error) console.error('Error inserting essay batch to Supabase:', error);
      });
    }
    return newDrafts;
  };

  const updateEssayDraft = (draftId, updates) => {
    const now = new Date().toISOString();
    setEssayDrafts(prev =>
      prev.map(draft =>
        draft.id === draftId
          ? { ...draft, ...updates, updatedAt: now }
          : draft
      )
    );
    // Debounced Supabase write for updates (typing in editor)
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (!currentUser?.id) return;
      const updatedDraft = { ...updates, updatedAt: now };
      const row = toRow(updatedDraft, currentUser.id);
      // Only send changed fields + id
      const { user_id, id, ...fields } = row;
      supabase.from('college_prep_essays')
        .update({ ...fields, updated_at: now })
        .eq('id', draftId)
        .eq('user_id', currentUser.id)
        .then(({ error }) => {
          if (error) console.error('Error updating essay in Supabase:', error);
        });
    }, 1500);
  };

  const deleteEssayDraft = (draftId) => {
    setEssayDrafts(prev => prev.filter(draft => draft.id !== draftId));
    if (currentUser?.id) {
      supabase.from('college_prep_essays')
        .delete()
        .eq('id', draftId)
        .eq('user_id', currentUser.id)
        .then(({ error }) => {
          if (error) console.error('Error deleting essay from Supabase:', error);
        });
    }
  };

  // Discovery Progress Management
  const updateDiscoveryProgress = (progress) => {
    setDiscoveryProgress(prev => ({ ...prev, ...progress }));
  };

  // Program Research Helpers
  const updateProgramResearch = (programId, researchData) => {
    updateProgram(programId, {
      researchData: {
        ...programs.find(p => p.id === programId)?.researchData,
        ...researchData
      },
      researchStatus: 'deep_research'
    });
  };

  const markProgramComplete = (programId) => {
    updateProgram(programId, {
      researchStatus: 'decided',
      status: 'researched'
    });
  };

  // Integration with family features
  const addToFamilyCalendar = async (programId, eventData) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return;

    // Add to family calendar through FamilyContext
    // This would integrate with the existing family calendar system
    console.log('Adding to family calendar:', { program, eventData });
  };

  const requestParentInput = async (programId, requestType, message) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return;

    // Send request to parents through family messaging system
    console.log('Requesting parent input:', { program, requestType, message });
  };

  const shareWithFamily = async (programId) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return;

    // Share program details with family
    console.log('Sharing with family:', program);
  };

  const value = {
    // Data
    interests,
    programs,
    researchTasks,
    discoveryProgress,
    timelineActions,
    essayDrafts,
    loading,
    
    // Constants
    ACADEMIC_INTERESTS,
    EXPERIENCE_GOALS,
    PROGRAM_CATEGORIES,
    
    // Core Actions
    updateInterests,
    addProgram,
    updateProgram,
    deleteProgram,
    addResearchTask,
    updateResearchTask,
    deleteResearchTask,
    
    // Timeline Actions
    addTimelineAction,
    updateTimelineAction,
    deleteTimelineAction,
    
    // Discovery Actions
    updateDiscoveryProgress,
    
    // Essay Draft Actions
    addEssayDraft,
    addEssayDraftBatch,
    updateEssayDraft,
    deleteEssayDraft,

    // Research Actions
    updateProgramResearch,
    markProgramComplete,
    
    // Family integration
    addToFamilyCalendar,
    requestParentInput,
    shareWithFamily,
    
    // Demo utilities
    clearAllData: async () => {
      setInterests({});
      setPrograms([]);
      setResearchTasks([]);
      setDiscoveryProgress({});
      setTimelineActions([]);
      setEssayDrafts([]);
      if (currentUser?.id) {
        localStorage.removeItem(`beckett-data-${currentUser.id}`);
        await supabase
          .from('college_prep_essays')
          .delete()
          .eq('user_id', currentUser.id);
      }
    }
  };

  return (
    <CollegePrepContext.Provider value={value}>
      {children}
    </CollegePrepContext.Provider>
  );
};